/**
 * Home Page
 * Editorial structure. No template patterns. No marketing fluff.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatPriceRange } from '../lib/utils';
import Button from '../components/common/Button';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Container, Section, Spacer } from '../components/primitives';
import { Display, Title, BodyLarge, Body, Label } from '../components/primitives/Typography';
import type { Project } from '../types';

export default function Home() {
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const projectsRes = await api.getFeaturedProjects(3);
        setFeaturedProjects(projectsRes.projects);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) return <PageLoader />;

  return (
    <main>
      {/* ========== Hero ========== */}
      <Section size="lg" background="gradient" className="pt-24 md:pt-40 pb-20 md:pb-32">
        <Container>
          <div className="max-w-4xl">
            <Display>
              Returning to India after a decade in tech?
            </Display>
            <BodyLarge className="mt-8 max-w-2xl">
              We help senior tech professionals navigate the financial, legal, and practical
              complexities of moving back. Tax optimization. Real estate. School admissions.
            </BodyLarge>
            <div className="mt-12">
              <Link to="/book">
                <Button variant="primary" size="lg">
                  Book a consultation
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== Who This Is For ========== */}
      <Section background="subtle">
        <Container>
          <div className="max-w-3xl">
            <Label>Who this is for</Label>
            <Title className="mt-4">
              Tech professionals in the US planning to return to India in the next 1–3 years.
            </Title>
            <div className="mt-12 space-y-6">
              <Body>
                You've been in the US for 8–15 years. You've built a career at companies like
                Google, Meta, Amazon, or startups. You have accumulated wealth in 401(k)s, RSUs,
                and real estate. Your children are school-age.
              </Body>
              <Body>
                You're not looking for generic advice. You need someone who understands the
                specific tax implications of FBAR, the timing of RSU vesting, the schools that
                actually work for kids coming from American curricula.
              </Body>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== Who This Is NOT For ========== */}
      <Section>
        <Container>
          <div className="max-w-3xl">
            <Label>Who this is not for</Label>
            <Title className="mt-4">
              Students. Recent graduates. People looking for immigration advice.
            </Title>
            <Body className="mt-8">
              We don't help with US visa applications, H1B transfers, or green card processes.
              We don't do generic financial planning. Our focus is narrow: helping established
              professionals return to India without losing money to avoidable mistakes.
            </Body>
          </div>
        </Container>
      </Section>

      {/* ========== Common Mistakes ========== */}
      <Section background="subtle">
        <Container>
          <div className="max-w-3xl">
            <Label>What we see repeatedly</Label>
            <Title className="mt-4">
              Three mistakes that cost returning NRIs lakhs.
            </Title>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-12 md:gap-8">
            <div>
              <span className="text-h2 font-medium text-text-tertiary">01</span>
              <h3 className="mt-4 text-h4 font-medium text-text-primary">
                Selling US assets at the wrong time
              </h3>
              <Body className="mt-3">
                Capital gains taxation differs dramatically based on your residency status.
                Timing matters more than most people realize.
              </Body>
            </div>

            <div>
              <span className="text-h2 font-medium text-text-tertiary">02</span>
              <h3 className="mt-4 text-h4 font-medium text-text-primary">
                Choosing schools based on rankings alone
              </h3>
              <Body className="mt-3">
                Children from American schools struggle with the Indian curriculum.
                The right school depends on your child's age and how long you've been away.
              </Body>
            </div>

            <div>
              <span className="text-h2 font-medium text-text-tertiary">03</span>
              <h3 className="mt-4 text-h4 font-medium text-text-primary">
                Buying property before understanding FEMA
              </h3>
              <Body className="mt-3">
                Real estate transactions for NRIs involve FEMA compliance, TDS,
                and repatriation rules that most agents don't understand.
              </Body>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== Services ========== */}
      <Section>
        <Container>
          <div className="max-w-3xl">
            <Label>What we do</Label>
            <Title className="mt-4">
              Two areas of focus. Nothing more.
            </Title>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-px bg-border">
            {/* Return to India */}
            <div className="bg-surface p-8 md:p-12">
              <h3 className="text-h3 font-medium text-text-primary">
                Return to India Consulting
              </h3>
              <Body className="mt-4">
                Tax planning that starts 2 years before your move. School research based on
                your specific situation. Healthcare and insurance setup.
              </Body>
              <ul className="mt-8 space-y-3">
                <li className="text-base text-text-secondary">US-India tax optimization</li>
                <li className="text-base text-text-secondary">401(k) and RSU planning</li>
                <li className="text-base text-text-secondary">School admissions strategy</li>
                <li className="text-base text-text-secondary">Healthcare transition</li>
              </ul>
              <div className="mt-10">
                <Link
                  to="/return-to-india"
                  className="text-base font-medium text-text-primary border-b border-text-primary pb-0.5 hover:text-primary-600 hover:border-primary-600 transition-colors"
                >
                  Learn more
                </Link>
              </div>
            </div>

            {/* Real Estate */}
            <div className="bg-surface p-8 md:p-12">
              <h3 className="text-h3 font-medium text-text-primary">
                Real Estate Advisory
              </h3>
              <Body className="mt-4">
                Apartments and villas in Bangalore and Hyderabad. We work with vetted builders
                only. No agents, no commissions from developers.
              </Body>
              <ul className="mt-8 space-y-3">
                <li className="text-base text-text-secondary">Property due diligence</li>
                <li className="text-base text-text-secondary">FEMA compliance</li>
                <li className="text-base text-text-secondary">NRI-specific documentation</li>
                <li className="text-base text-text-secondary">Investment strategy</li>
              </ul>
              <div className="mt-10">
                <Link
                  to="/real-estate"
                  className="text-base font-medium text-text-primary border-b border-text-primary pb-0.5 hover:text-primary-600 hover:border-primary-600 transition-colors"
                >
                  View properties
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== Featured Properties ========== */}
      {featuredProjects.length > 0 && (
        <Section background="subtle">
          <Container>
            <div className="flex justify-between items-end">
              <div>
                <Label>Properties</Label>
                <Title className="mt-4">Currently available</Title>
              </div>
              <Link
                to="/real-estate"
                className="hidden md:block text-small font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                View all
              </Link>
            </div>

            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {featuredProjects.map((project) => (
                <Link key={project.id} to={`/real-estate/${project.slug}`} className="group block bg-white shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                  <div className="aspect-[4/3] bg-surface-muted relative overflow-hidden">
                    {project.hero_image ? (
                      <img
                        src={project.hero_image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-text-tertiary">
                        <span className="text-small">No image</span>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <span className="text-caption font-medium uppercase tracking-wider text-text-tertiary">
                      {project.project_type} · {project.city}
                    </span>
                    <h3 className="mt-1 text-h4 font-medium text-text-primary group-hover:text-primary-600 transition-colors">
                      {project.title}
                    </h3>
                    <p className="mt-1 text-small text-text-secondary">
                      {project.location}
                    </p>
                    <p className="mt-2 text-base font-medium text-primary-600">
                      {formatPriceRange(project.price_min, project.price_max)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-10 md:hidden">
              <Link to="/real-estate">
                <Button variant="secondary">View all properties</Button>
              </Link>
            </div>
          </Container>
        </Section>
      )}

      {/* ========== Founder Section ========== */}
      <Section>
        <Container narrow>
          <div className="border-l-2 border-border pl-8 md:pl-12">
            <blockquote className="text-h3 md:text-h2 font-medium text-text-primary leading-snug">
              "I returned to India in 2019 after 12 years in the Bay Area. I made every
              mistake possible—tax timing, school choice, property purchase. Anvayee exists
              so you don't have to."
            </blockquote>
            <div className="mt-8">
              <p className="text-base font-medium text-text-primary">Founder</p>
              <p className="text-small text-text-secondary">Former tech lead at Google, returned 2019</p>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== CTA ========== */}
      <Section background="gradient-teal">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <Title>
              Start with a conversation.
            </Title>
            <Body className="mt-4">
              30-minute consultation. No sales pitch. We'll tell you if we can help.
            </Body>
            <div className="mt-10">
              <Link to="/book">
                <Button variant="primary" size="lg">
                  Book a consultation
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      <Spacer size="sm" />
    </main>
  );
}
